<template>
    <!--
        A YouTube embed that requests nothing from YouTube until the visitor presses play.

        The poster comes from the cookieless i.ytimg.com host and the <iframe> is only
        created on click, so YouTube's four tracking cookies — VISITOR_INFO1_LIVE, YSC,
        __Secure-YNID and __Secure-ROLLOUT_TOKEN — never reach anyone who does not play the
        video. The player that finally loads is on youtube-nocookie.com, which covers the
        visitor who does. That pairing is what clears Chrome's "uses third-party cookies"
        audit; it also keeps YouTube's player JS out of the initial paint.

        Google Drive links are supported but have no poster endpoint, so they skip the facade
        and embed directly rather than showing a play button over a broken image. Drive is not
        one of the cookie offenders.
    -->
    <div v-if="embedUrl" class="relative w-full h-full">
        <button
            v-if="showFacade"
            type="button"
            class="group relative block w-full h-full p-0 border-0 bg-transparent cursor-pointer"
            :aria-label="playLabel"
            @click="play"
        >
            <!-- Decorative: the button's aria-label already names the video. -->
            <!-- No loading="lazy" here: this poster is frequently the LCP element (e.g. the
                 homepage hero), and lazy-loading an above-the-fold image has caused a real
                 regression before. -->
            <img
                :src="posterUrl"
                alt=""
                aria-hidden="true"
                :class="['w-full h-full object-cover', mediaClass]"
                fetchpriority="high"
                @load="checkPoster"
                @error="downgradePoster"
            />
            <span class="absolute inset-0 flex items-center justify-center">
                <span
                    class="w-16 h-16 rounded-full flex items-center justify-center shadow-lg transition-opacity group-hover:opacity-90 group-focus-visible:ring-4 group-focus-visible:ring-white"
                    style="background:#ff0000"
                >
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="white" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg>
                </span>
            </span>
        </button>
        <iframe
            v-else
            ref="player"
            :src="playerUrl"
            :title="title"
            :class="['w-full h-full border-0', mediaClass]"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
            referrerpolicy="strict-origin-when-cross-origin"
            allowfullscreen
            loading="lazy"
        ></iframe>
    </div>
</template>

<script>
// watch?v=ID, youtu.be/ID, embed/ID, /v/ID, shorts/ID. Editors paste all of these — and a
// raw watch link cannot be framed at all (YouTube refuses it), so parsing out the id is what
// makes a pasted link work, not just what makes it cookieless.
const YOUTUBE_ID = /(?:youtube\.com\/(?:watch\?(?:.*&)?v=|embed\/|v\/|shorts\/)|youtu\.be\/)([\w-]{11})/;
const DRIVE_ID = /drive\.google\.com\/file\/d\/([\w-]+)/;

export default {
    props: {
        // Any YouTube or Google Drive link. Unrecognised values render nothing.
        videoUrl: {
            type: String,
            default: ''
        },
        // Accessible name for the player and the play button. May contain markup, since
        // callers pass headings that are rendered with v-html elsewhere.
        label: {
            type: String,
            default: ''
        },
        // Applied to both the poster and the iframe so the two are shaped identically.
        // Callers that already clip the box (overflow-hidden) can leave this blank.
        mediaClass: {
            type: String,
            default: ''
        }
    },
    data() {
        return {
            isPlaying: false,
            // maxresdefault only exists for videos uploaded in HD. See downgradePoster.
            posterQuality: 'maxresdefault'
        }
    },
    computed: {
        youtubeId() {
            const match = (this.videoUrl || '').trim().match(YOUTUBE_ID);
            return match ? match[1] : null;
        },

        // Only the id is kept from whatever the editor pasted, so the domain here is always
        // the one that ends up in the DOM.
        embedUrl() {
            const url = (this.videoUrl || '').trim();
            if (!url) return null;

            if (this.youtubeId) {
                return `https://www.youtube-nocookie.com/embed/${this.youtubeId}`;
            }

            const drive = url.match(DRIVE_ID);
            if (drive) {
                return `https://drive.google.com/file/d/${drive[1]}/preview`;
            }

            return null;
        },

        // i.ytimg.com rather than img.youtube.com: the latter is a youtube.com host and so
        // participates in those cookies, while i.ytimg.com is the dedicated static host and
        // stays a plain image request.
        posterUrl() {
            return this.youtubeId
                ? `https://i.ytimg.com/vi/${this.youtubeId}/${this.posterQuality}.jpg`
                : null;
        },

        showFacade() {
            return this.posterUrl !== null && !this.isPlaying;
        },

        // autoplay is added only for a real click, so the deferred player starts immediately
        // instead of making the visitor press play twice. A Drive embed never gets it.
        playerUrl() {
            if (!this.embedUrl) return null;

            return this.isPlaying ? `${this.embedUrl}?autoplay=1` : this.embedUrl;
        },

        cleanLabel() {
            return this.label ? this.label.replace(/<[^>]*>/g, '').trim() : '';
        },

        title() {
            return this.cleanLabel || 'Embedded content';
        },

        playLabel() {
            return this.cleanLabel ? `Play video: ${this.cleanLabel}` : 'Play video';
        }
    },
    methods: {
        // Focus moves to the iframe so a keyboard user lands on the player they just started
        // rather than back at the top of the document.
        play() {
            this.isPlaying = true;
            this.$nextTick(() => {
                if (this.$refs.player) {
                    this.$refs.player.focus();
                }
            });
        },

        downgradePoster() {
            if (this.posterQuality !== 'hqdefault') {
                this.posterQuality = 'hqdefault';
            }
        },

        // A missing maxresdefault normally 404s, but some edges answer 200 with a 120x90 grey
        // placeholder instead. Both end up on hqdefault, which every video has.
        checkPoster(event) {
            if (event.target.naturalWidth <= 120) {
                this.downgradePoster();
            }
        }
    }
}
</script>
